import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import cn from "classnames";
import { Tabs } from "components";
import { PeopleOutreach } from "./PeopleOutreach";
import { getPeopleActivityExport, getPeopleInteractionExport, getPeopleOutreachExport } from "../actions/peopleActions";
import ReportingExport from "components/reportingExport/ReportingExport";
import ReportingFilter from "components/reportingFilter/ReportingFilter";
import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";
import {
  ChartWrapper,
  GroupedBarChart,
  type GroupedBarProps,
  LineChart,
  type LinechartProps,
  getDateFormatByCount,
} from "components/charts";

import { usePerformanceBreadcrumbs } from "features/Library/Common/Hooks/usePerformanceBreadcrumbs";
import { usePerformanceFilter } from "features/Library/Common/Hooks/usePerformanceFilter";
import {
  convertInteractionDisplayName,
  createDateRange,
  getFormattedTimeStringFromPeriod,
  lineChartFactory,
  noData,
  validLineData,
} from "features/Library/Common/utils/performanceUtils";
import { reportingColors } from "features/Library/Common/utils/constants";
import { sharedAccountReportingLineProps } from "features/Reporting/Content/shared";
import { sharedGoogleReportingBarProps } from "features/Reporting/Google/utils/shared";
import { usePeopleInteractionTableQuery, usePeopleOutreachTableQuery } from "../queries";

import { useGoogleTooltipFormats } from "features/Reporting/Google/utils/useGoogleTooltipFormats";
import { useChartPeriodMeasure, CHART_PERIOD } from "hooks/useChartPeriodMeasure";

import "features/Library/Common/utils/performanceSCSSUtils.scss";
import "../peopleStyles.scss";
import { type PerformancePeriodQueryContext } from "features/Library/Common/models";
import { peopleReportingService } from "features/Reporting/services/peopleReportingService";
import moment from "moment";
import PeopleActivityDrilldown from "./PeopleActivityDrilldown";
import type { RootState, AppDispatch } from "features/Application/globaltypes/redux";
import { setHasDataToExport, setExportAction } from "features/Reporting/state/export/exportSlice";
import { bindAction } from "interfaces";
import { connect, type ConnectedProps } from "react-redux";
import { UserInteraction } from "./UserInteraction";
import { type UserAssetTypes } from "features/People/Users/Performance/types";
import { roundToTwoDigits } from "features/Library/Common/utils/roundToTwoDigits";
import { useAccountFilterShouldShow } from "features/Reporting/Content/queries/useAccountFilterShouldShow";

const colorRange = [reportingColors.greenDark, reportingColors.blue];

const getLineChart = async ({
  queryKey,
  signal,
}: PerformancePeriodQueryContext): Promise<{
  lineProps: LinechartProps;
  barProps: GroupedBarProps<{ userInteraction: number; platformOutreach: number }>;
}> => {
  const { data } = await peopleReportingService.activity.getLineChart({ ...queryKey[1], period: queryKey[2] }, signal!);
  const factory = lineChartFactory(data);

  const barData = data.map((d) => ({
    group: moment(d.Date, "YYYY-MM-DD").format("YYYY-MM-DD"),
    userInteraction: d.PlatformInteraction,
    platformOutreach: d.PlatformOutreach,
  }));

  return {
    lineProps: {
      ...sharedAccountReportingLineProps,
      xData: [factory.Date, factory.Date],
      yData: [factory.PlatformOutreach, factory.PlatformInteraction],
      xFormatterFunc: getDateFormatByCount(factory?.Date?.length),
      colorRange,
      tooltipFormatter: (_x, y) => roundToTwoDigits(y),
    },
    barProps: {
      ...sharedGoogleReportingBarProps,
      data: barData,
      order: ["platformOutreach", "userInteraction"],
      colorRange,
    },
  };
};
const lineWrapperProps = {
  titles: ["Activity - Line Chart", "Activity - Bar Chart"],
  showLegend: true,
  legendLabels: ["Platform Outreach", "User Interactions"],
  colorRange,
};

type Categories = "outreach" | "interaction";

export type Props = PropsFromRedux & {
  usesDropdownMenuNavigation?: boolean;
  renderHeader?: () => JSX.Element | null;
  childAccountId?: number;
  hasCreateAccountsAddOn?: boolean;
  disableLinks?: boolean;
};

export const PeopleActivityReport = ({
  accountId,
  getPeopleActivityExport,
  getPeopleInteractionExport,
  getPeopleOutreachExport,
  setExport,
  setIsExportEnabled,
  usesDropdownMenuNavigation,
  renderHeader,
  childAccountId,
  hasCreateAccountsAddOn,
  accountType,
  disableLinks,
}: Props) => {
  const accountFilterShouldShow = useAccountFilterShouldShow(accountId, accountType, true);

  const initialOverrides = useMemo(() => {
    if (usesDropdownMenuNavigation && childAccountId) {
      return { accounts: [childAccountId] as number[], includeMyData: false };
    }
    return undefined;
  }, [usesDropdownMenuNavigation, childAccountId]);

  const [filter, setFilter] = usePerformanceFilter({
    accountFilterShouldShow: false,
    includeAccountsDropdown: accountFilterShouldShow,
    supportsSPType: true,
    accountId,
    initialOverrides,
  });

  const [chartState, setChartState] = useState("Activity - Line Chart");
  const [activeTab, setActiveTab] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState<Categories>();
  const [selectedType, setSelectedType] = useState<UserAssetTypes | undefined>(undefined);
  const dateRange = useMemo(() => createDateRange(filter.dateFrom, filter.dateTo), [filter.dateFrom, filter.dateTo]);

  const { domElements, registerBreadcrumb, hasBreadcrumbsToDisplay } = usePerformanceBreadcrumbs();

  const [chartPeriod, measureRef] = useChartPeriodMeasure(
    dateRange,
    sharedAccountReportingLineProps.margins!.left + sharedAccountReportingLineProps.margins!.right,
  );

  const { groupedBarFormatter } = useGoogleTooltipFormats(chartPeriod, dateRange);

  const lineChartQuery = useQuery({
    queryFn: getLineChart,
    queryKey: ["people activity line chart", filter, chartPeriod],
    enabled: chartPeriod !== CHART_PERIOD.UNSET,
  });

  const handleCategorySelection = useCallback(
    (text: UserAssetTypes, category: "outreach" | "interaction") => {
      setActiveTab(category === "outreach" ? 0 : 1);
      registerBreadcrumb({
        text: convertInteractionDisplayName(text),
        action: () => {
          setSelectedType(undefined);
          setSelectedCategory(undefined);
        },
      });
      setSelectedType(text);
      setSelectedCategory(category);
    },
    [registerBreadcrumb],
  );

  const handleTabChange = useCallback((_e: unknown, data: { activeIndex?: number }) => {
    setActiveTab(Number(data.activeIndex ?? 0));
  }, []);

  // Used for over 1,000,000 rows validation
  const interactionsQuery = usePeopleInteractionTableQuery(filter);

  const hasOverMillionInteractions = useMemo(() => {
    return (
      interactionsQuery.isFetched && interactionsQuery.data?.rows?.some((item) => item.InteractionCount > 1_000_000)
    );
  }, [interactionsQuery]);

  // Setting appropriate export method
  useEffect(() => {
    if (selectedCategory) {
      setExport({
        // @ts-ignore
        method: selectedCategory === "interaction" ? getPeopleInteractionExport : getPeopleOutreachExport,
        args: [{ ...filter, activityType: selectedType }],
      });
    } else {
      setExport({
        // @ts-ignore
        method: getPeopleActivityExport,
        args: [filter, hasOverMillionInteractions],
      });
    }
  }, [
    filter,
    getPeopleActivityExport,
    getPeopleInteractionExport,
    getPeopleOutreachExport,
    selectedCategory,
    selectedType,
    setExport,
    hasOverMillionInteractions,
  ]);

  // Determining export button visiblity
  const peopleInteractionTableQuery = usePeopleInteractionTableQuery(filter);
  const peopleOutreachTableQuery = usePeopleOutreachTableQuery(filter);

  useEffect(() => {
    if (!selectedCategory) {
      const interactionTotalCount = Number(peopleInteractionTableQuery.data?.totalCount ?? 0);
      const outreachTotalCount = Number(peopleOutreachTableQuery.data?.totalCount ?? 0);

      const hasInteractionData = peopleInteractionTableQuery.isSuccess && interactionTotalCount > 0;
      const hasOutreachData = peopleOutreachTableQuery.isSuccess && outreachTotalCount > 0;

      setIsExportEnabled({
        hasDataToExport: hasInteractionData || hasOutreachData,
        tooltipContent: "No data available for export. There is no activity data.",
      });
    }
  }, [peopleInteractionTableQuery, peopleOutreachTableQuery, selectedCategory, setIsExportEnabled]);

  const activitySection = useMemo(() => {
    if (!lineChartQuery.data) return null;

    return chartState === "Activity - Line Chart" ? (
      <LineChart {...lineChartQuery.data.lineProps} />
    ) : (
      <GroupedBarChart
        {...lineChartQuery.data.barProps}
        tooltipFormatter={groupedBarFormatter}
        xTickFormatter={(d) => getFormattedTimeStringFromPeriod(d.toString(), chartPeriod, dateRange)}
      />
    );
  }, [chartState, lineChartQuery.data, chartPeriod, dateRange, groupedBarFormatter]);

  const ignoreInCountAndHide = useMemo(() => {
    const ignore = ["type"];
    if (usesDropdownMenuNavigation) {
      ignore.push("accounts", "includeMyData");
    }
    return ignore;
  }, [usesDropdownMenuNavigation]);

  return (
    <div>
      <div className="performanceRoot">
        <div className={cn("performanceHeader", "peopleActivityHeader")}>
          {hasBreadcrumbsToDisplay ? (
            <div>{domElements}</div>
          ) : (
            <div className={"performanceHeader peopleActivityFilters"}>
              {usesDropdownMenuNavigation && renderHeader ? (
                renderHeader()
              ) : (
                <h2 className="performanceTitle">Summary</h2>
              )}
              <ReportingFilter
                currentFilter={filter}
                callback={setFilter}
                includeAccounts={false}
                includeAccountsDropdown={accountFilterShouldShow}
                ignoreInCountAndHide={ignoreInCountAndHide}
                supportsSPType
              />
            </div>
          )}
          <ReportingExport />
        </div>

        {selectedCategory ? (
          <PeopleActivityDrilldown
            dateFilter={filter}
            selectedType={selectedType!}
            selectedCategory={selectedCategory}
            accountId={accountId}
            childAccountId={childAccountId}
            hasCreateAccountsAddOn={hasCreateAccountsAddOn}
            disableLinks={disableLinks}
          />
        ) : (
          <>
            <div className="lineChartContainer" ref={measureRef}>
              <ChartWrapper {...lineWrapperProps} onChange={setChartState}>
                <RequestStatusRenderer state={lineChartQuery.status}>
                  {lineChartQuery.isSuccess && validLineData(lineChartQuery.data.lineProps.yData)
                    ? activitySection
                    : noData(filter)}
                </RequestStatusRenderer>
              </ChartWrapper>
            </div>
            <Tabs activeIndex={activeTab} onTabChange={handleTabChange}>
              <Tabs.Pane label="Platform Outreach" id="0">
                <PeopleOutreach
                  dateFilter={filter}
                  setSelectedDrilldown={(type: UserAssetTypes) => handleCategorySelection(type, "outreach")}
                />
              </Tabs.Pane>
              <Tabs.Pane label="User Interactions" id="1">
                <UserInteraction
                  dateFilter={filter}
                  setSelectedDrilldown={(type: UserAssetTypes) => handleCategorySelection(type, "interaction")}
                />
              </Tabs.Pane>
            </Tabs>
          </>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  accountId: state.userProfile.accountId,
  accountType: state.userProfile.accountTypeId,
});

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    getPeopleOutreachExport: bindAction(getPeopleOutreachExport, dispatch),
    getPeopleInteractionExport: bindAction(getPeopleInteractionExport, dispatch),
    getPeopleActivityExport: bindAction(getPeopleActivityExport, dispatch),
    setIsExportEnabled: bindAction(setHasDataToExport, dispatch),
    setExport: bindAction(setExportAction, dispatch),
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(PeopleActivityReport);
export default ConnectedComponent;
